import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from "../api";
import { 
  Container,
  Grid, // Added Grid import
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Chip,
  IconButton,
  useMediaQuery
} from '@mui/material';
import {
  Code as WebIcon,
  PhoneAndroid as MobileIcon,
  Computer as SupportIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Web as PortfolioIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

// Try to import react-slick dynamically
let Slider;
try {
  Slider = require('react-slick').default;
  require('slick-carousel/slick/slick.css');
  require('slick-carousel/slick/slick-theme.css');
} catch (e) {
  console.warn('react-slick not available, using fallback grid');
}

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const [sliderRef, setSliderRef] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    const fetchServices = async () => {
      try {
        // const response = await axios.get('http://localhost:5001/api/services');
        // const response = await axios.get('/api/services');
        const response = await api.get('/api/services');
        setServices(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const getServiceIcon = (iconName) => {
    switch(iconName) {
      case 'web': return <WebIcon fontSize="large" />;
      case 'mobile': return <MobileIcon fontSize="large" />;
      case 'support': return <SupportIcon fontSize="large" />;
      case 'analytics': return <AnalyticsIcon fontSize="large" />;
      case 'security': return <SecurityIcon fontSize="large" />;
      case 'portfolio': return <PortfolioIcon fontSize="large" />;
      default: return <WebIcon fontSize="large" />;
    }
  };

  const getServiceColor = (index) => {
    const colors = ['primary', 'secondary', 'info', 'success', 'warning', 'error'];
    return colors[index % colors.length];
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
      <Typography>Loading services...</Typography>
    </Container>
  );
  
  if (error) return (
    <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
      <Typography color="error">Error: {error}</Typography>
    </Container>
  );

  // Fallback grid layout if slider isn't available
  if (!Slider) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: 6, fontWeight: 700 }}>
          Our Services
        </Typography>
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={service._id}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: theme.palette[getServiceColor(index)].main,
                      color: theme.palette[getServiceColor(index)].contrastText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mx: 'auto'
                    }}
                  >
                    {getServiceIcon(service.icon)}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  <Chip 
                    label={`$${service.price}`} 
                    color={getServiceColor(index)} 
                    variant="outlined" 
                    sx={{ fontWeight: 600 }} 
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    cssEase: 'ease-in-out'
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8, position: 'relative' }}>
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: 6, fontWeight: 700 }}>
        Our Services
      </Typography>

      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={() => sliderRef?.slickPrev()}
          sx={{
            position: 'absolute',
            left: { xs: -40, sm: -50 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': { bgcolor: 'background.paper' }
          }}
        >
          <ChevronLeft fontSize="large" />
        </IconButton>

        <Slider ref={setSliderRef} {...sliderSettings}>
          {services.map((service, index) => (
            <Box key={service._id} sx={{ px: 1 }}>
              <Card sx={{ 
                height: '100%', 
                mx: 'auto', 
                maxWidth: 400,
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: theme.palette[getServiceColor(index)].main,
                      color: theme.palette[getServiceColor(index)].contrastText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mx: 'auto'
                    }}
                  >
                    {getServiceIcon(service.icon)}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  <Chip 
                    label={`$${service.price}`} 
                    color={getServiceColor(index)} 
                    variant="outlined" 
                    sx={{ fontWeight: 600 }} 
                  />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Slider>

        <IconButton
          onClick={() => sliderRef?.slickNext()}
          sx={{
            position: 'absolute',
            right: { xs: -40, sm: -50 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': { bgcolor: 'background.paper' }
          }}
        >
          <ChevronRight fontSize="large" />
        </IconButton>
      </Box>
    </Container>
  );
};

export default Services;